import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import { isQ7_1Complete } from '@/lib/q7_1Storage';
import { isQ7_2StepComplete } from '@/lib/q7_2Storage';
import {
  getQ7_3Options,
  readQ7_3Answers,
  writeQ7_3Answers,
  type Q7_3Option,
} from '@/lib/q7_3Storage';
import { clearQ7_4Answers } from '@/lib/q7_4Storage';
import { isSmokerProfileComplete } from '@/lib/smokerProfileStorage';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import type { AppTranslations } from '@/locales';
import { useAppSelector } from '@/stores/hooks';
import {
  selectCodeBrand,
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

/** Match /7_1: bold white, body-size (not panel 20px). */
const checkboxLabelClass =
  'flex cursor-pointer items-start gap-3 text-left text-base font-bold leading-snug text-white';

const checkboxClass =
  'mt-0.5 h-5 w-5 shrink-0 rounded border-2 border-white/50 bg-black/20 accent-[#c9a882] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80';

/** Display label for a canonical (EN) /7_3 option. */
function q7_3OptionLabel(option: Q7_3Option, t: AppTranslations['q7_3']): string {
  switch (option) {
    case 'Choose this one again':
      return t.optionChooseAgain;
    case 'Go back to my usual choice':
      return t.optionGoBackUsual;
    case 'Try another option':
      return t.optionTryAnother;
    case 'Decide depending on the occasion':
      return t.optionDependOccasion;
    case 'Taste/flavour':
      return t.optionTasteFlavour;
    case 'Overall experience':
      return t.optionOverallExperience;
    case 'Strength/intensity':
      return t.optionStrengthIntensity;
    case 'Appearance/pack':
      return t.optionAppearancePack;
    case 'Format':
      return t.optionFormat;
    case 'Availability':
      return t.optionAvailability;
    case 'Nothing in particular':
      return t.optionNothingInParticular;
    default:
      return option;
  }
}

export default function Q7_3() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const codeBrand = useAppSelector(selectCodeBrand);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const options = useMemo(() => getQ7_3Options(codeBrand), [codeBrand]);
  const [selected, setSelected] = useState<Q7_3Option | null>(
    () => readQ7_3Answers()?.selected ?? null
  );
  const navigateAfterCheckRef = useRef(false);

  useEffect(() => {
    if (!navigateAfterCheckRef.current) {
      return;
    }
    navigateAfterCheckRef.current = false;
    navigate('/7_4');
  }, [selected, navigate]);

  const selectOption = (label: Q7_3Option) => {
    setSelected((prev) => {
      if (prev === label) {
        return prev;
      }
      writeQ7_3Answers({ selected: label });
      if (codeBrand !== 'Dice') {
        clearQ7_4Answers();
      }
      navigateAfterCheckRef.current = true;
      return label;
    });
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!isSmokerProfileComplete()) {
    return <Navigate to="/smokerprofile" replace />;
  }
  if (!isQ7_1Complete()) {
    return <Navigate to="/7_1" replace />;
  }
  if (!isQ7_2StepComplete(codeBrand)) {
    return <Navigate to="/7_2" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] min-h-0 flex-1 flex-col overflow-hidden">
        {/* pt not mt: margin on flex-1 child is clipped by overflow-hidden (cuts bottom radius) */}
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[7vh] pb-4">
          {/* Panel fills viewport; answers scroll inside so bottom radius stays visible */}
          <SurveyBrownPanelInset className="flex min-h-0 flex-1 flex-col">
            <SurveyBrownPanel className="!flex !h-full !min-h-0 !flex-col !overflow-hidden !px-4 !pt-4 !pb-4">
              <p className="m-0 shrink-0 break-words text-left text-[19px] font-bold text-white sm:text-[20px]">
                {codeBrand === 'Dice' ? t.q7_3.diceQuestion : t.q7_3.question}
              </p>
              <div className="mt-[4.5rem] min-h-0 flex-1 overflow-y-auto sm:mt-[5.25rem]">
                <div className="flex flex-col gap-[1.875rem] pb-2 sm:gap-[2.25rem]">
                  {options.map((option) => {
                    const id = `q73-${option.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-']/g, '')}`;
                    const checked = selected === option;
                    return (
                      <label key={option} htmlFor={id} className={checkboxLabelClass}>
                        <input
                          id={id}
                          type="checkbox"
                          checked={checked}
                          onChange={() => selectOption(option)}
                          className={checkboxClass}
                        />
                        <span>{q7_3OptionLabel(option, t.q7_3)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
